/**
 * Timezone utility functions for KSP3PLHQ
 * Handles timezone conversion and provides common timezones
 */

// List of common timezones with display name and IANA identifier
export const commonTimezones = [
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)', abbr: 'CT' },
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)', abbr: 'ET' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)', abbr: 'MT' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)', abbr: 'PT' },
  { value: 'America/Phoenix', label: 'Arizona Time (No DST)', abbr: 'AZ' },
  { value: 'UTC', label: 'Universal Time Coordinated (UTC)', abbr: 'UTC' },
  { value: 'Europe/London', label: 'UK Time (GMT/BST)', abbr: 'UK' },
  { value: 'Europe/Paris', label: 'Central European Time (CET/CEST)', abbr: 'CET' },
  { value: 'Asia/Tokyo', label: 'Japan Time (JST)', abbr: 'JST' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AEST/AEDT)', abbr: 'AET' }
];

/**
 * Get the browser's timezone
 * @returns {string} The IANA timezone identifier
 */
export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
};

/**
 * Format a date in the specified timezone
 * @param {Date|string|number} date - The date to format
 * @param {string} timezone - The IANA timezone identifier
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} The formatted date string
 */
export const formatInTimezone = (date, timezone = 'UTC', options = {}) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Default options for date formatting
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    ...options
  };
  
  try {
    // Format the date in the specified timezone
    return new Intl.DateTimeFormat('en-US', {
      ...defaultOptions,
      timeZone: timezone
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback to UTC if the timezone is invalid
    return new Intl.DateTimeFormat('en-US', {
      ...defaultOptions,
      timeZone: 'UTC'
    }).format(dateObj);
  }
};

/**
 * Format a date in the specified timezone (date only)
 * @param {Date|string|number} date - The date to format
 * @param {string} timezone - The IANA timezone identifier
 * @returns {string} The formatted date string (without time)
 */
export const formatDateOnly = (date, timezone = 'UTC') => {
  return formatInTimezone(date, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: undefined,
    minute: undefined,
    timeZoneName: undefined
  });
};

/**
 * Format a date in the specified timezone (time only)
 * @param {Date|string|number} date - The date to format
 * @param {string} timezone - The IANA timezone identifier
 * @returns {string} The formatted time string
 */
export const formatTimeOnly = (date, timezone = 'UTC') => {
  return formatInTimezone(date, timezone, {
    year: undefined,
    month: undefined,
    day: undefined,
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

/**
 * Format a UTC timestamp in the specified timezone (time only)
 * @param {Date|string|number} utcTimestamp - The UTC timestamp to format
 * @param {string} timezone - The IANA timezone identifier
 * @returns {string} The formatted time string
 */
export const formatUtcTimeOnly = (utcTimestamp, timezone = 'America/Chicago') => {
  if (!utcTimestamp) return '';
  
  // Ensure we're working with a proper UTC date
  let dateObj;
  if (utcTimestamp instanceof Date) {
    dateObj = utcTimestamp;
  } else {
    // If it's a string without timezone info, treat it as UTC
    const dateStr = utcTimestamp.toString();
    if (dateStr.includes('Z') || dateStr.includes('+') || dateStr.includes('-')) {
      dateObj = new Date(utcTimestamp);
    } else {
      // Assume it's UTC if no timezone info
      dateObj = new Date(utcTimestamp + 'Z');
    }
  }
  
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: timezone
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting UTC time:', error);
    // Fallback to CST
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: 'America/Chicago'
    }).format(dateObj);
  }
};

/**
 * Get the timezone abbreviation from the timezone value
 * @param {string} timezone - The IANA timezone identifier
 * @returns {string} The timezone abbreviation or the timezone itself if not found
 */
export const getTimezoneAbbreviation = (timezone) => {
  const found = commonTimezones.find(tz => tz.value === timezone);
  return found ? found.abbr : timezone;
};

// ==== DATETIME PICKER UTILITIES ====

/**
 * Format datetime for HTML datetime-local input (YYYY-MM-DDTHH:MM)
 * @param {Date|string|number} datetime - The datetime to format
 * @returns {string} The formatted datetime string for input
 */
export const formatForDateTimeInput = (datetime) => {
  if (!datetime) return '';
  
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return '';
  
  // Convert to local timezone and format for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parse datetime-local input value to ISO string
 * @param {string} inputValue - The datetime-local input value
 * @returns {string|null} The ISO string or null if invalid
 */
export const parseFromDateTimeInput = (inputValue) => {
  if (!inputValue) return null;
  
  try {
    // datetime-local format: YYYY-MM-DDTHH:MM
    const date = new Date(inputValue);
    if (isNaN(date.getTime())) return null;
    
    return date.toISOString();
  } catch (error) {
    console.error('Error parsing datetime input:', error);
    return null;
  }
};

/**
 * Format datetime for display (human-readable)
 * @param {Date|string|number} datetime - The datetime to format
 * @returns {string} The formatted display string
 */
export const formatForDisplay = (datetime) => {
  if (!datetime) return '';
  
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return '';
  
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

/**
 * Check if a datetime is overdue (past current time)
 * @param {Date|string|number} datetime - The datetime to check
 * @returns {boolean} True if overdue
 */
export const isOverdue = (datetime) => {
  if (!datetime) return false;
  
  const date = new Date(datetime);
  const now = new Date();
  
  return date < now;
};

/**
 * Check if a datetime is today
 * @param {Date|string|number} datetime - The datetime to check
 * @returns {boolean} True if today
 */
export const isToday = (datetime) => {
  if (!datetime) return false;
  
  const date = new Date(datetime);
  const today = new Date();
  
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Get days until/since datetime (negative = overdue, positive = future)
 * @param {Date|string|number} datetime - The datetime to check
 * @returns {number|null} Days until (positive) or since (negative)
 */
export const getDaysUntil = (datetime) => {
  if (!datetime) return null;
  
  const date = new Date(datetime);
  const now = new Date();
  
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Get relative time string (e.g., "in 3 days", "2 hours ago")
 * @param {Date|string|number} datetime - The datetime to format
 * @returns {string} The relative time string
 */
export const getRelativeTime = (datetime) => {
  if (!datetime) return '';
  
  const date = new Date(datetime);
  const now = new Date();
  
  const diffMs = date - now;
  const absDiffMs = Math.abs(diffMs);
  
  const minutes = Math.floor(absDiffMs / (1000 * 60));
  const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
  const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
  
  if (days > 0) {
    return diffMs > 0 ? `in ${days} day${days > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return diffMs > 0 ? `in ${hours} hour${hours > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    return diffMs > 0 ? `in ${minutes} minute${minutes > 1 ? 's' : ''}` : `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else {
    return 'now';
  }
};

/**
 * Get timeline status with colors
 * @param {Date|string|number} startDatetime - The start datetime
 * @param {Date|string|number} endDatetime - The end datetime
 * @param {boolean} isCompleted - Whether the item is completed
 * @returns {object} Status info with colors
 */
export const getTimelineStatusInfo = (startDatetime, endDatetime, isCompleted = false) => {
  const now = new Date();
  const start = startDatetime ? new Date(startDatetime) : null;
  const end = endDatetime ? new Date(endDatetime) : null;
  
  if (isCompleted) {
    return {
      status: 'completed',
      label: 'Completed',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/20'
    };
  }
  
  if (end && end < now) {
    return {
      status: 'overdue',
      label: 'Overdue',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20'
    };
  }
  
  if (start && start <= now && end && end >= now) {
    return {
      status: 'active',
      label: 'Active',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20'
    };
  }
  
  if (start && start > now) {
    return {
      status: 'scheduled',
      label: 'Scheduled',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/20'
    };
  }
  
  return {
    status: 'unscheduled',
    label: 'Unscheduled',
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/20'
  };
};
